'use client';

import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { TrendingUp, Users, Star } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { getScoreColor, getScoreBadge } from '@/lib/utils';

interface OverallStats {
  avgEffort: number;
  avgEngagement: number;
  avgCompetition: number;
  avgAttitude: number;
  totalEvaluations: number;
}

interface ChartDataPoint {
  date: string;
  effort: number;
  engagement: number;
  competitionLevel: number;
  attitude: number;
  count: number;
}

interface PlayerPerformance {
  playerId: string;
  name: string;
  overall: number;
  effort: number;
  engagement: number;
  competitionLevel: number;
  attitude: number;
  evalCount: number;
}

interface OrgAnalyticsDashboardProps {
  overallStats: OverallStats | null;
  chartData: ChartDataPoint[];
  topPerformers: PlayerPerformance[];
  needsAttention: PlayerPerformance[];
}

// Find the lowest scoring category for a player
function getLowestCategory(player: PlayerPerformance): string {
  const scores = [
    { name: 'Effort', value: player.effort },
    { name: 'Engagement', value: player.engagement },
    { name: 'Competition', value: player.competitionLevel },
    { name: 'Attitude', value: player.attitude },
  ];
  return scores.reduce((min, s) => (s.value < min.value ? s : min)).name;
}

export function OrgAnalyticsDashboard({
  overallStats,
  chartData,
  topPerformers,
  needsAttention,
}: OrgAnalyticsDashboardProps) {
  if (!overallStats || chartData.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Organization Analytics</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground text-center py-8">
            No evaluation data available yet. Analytics will appear as coaches create evaluations.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Overall Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-sm text-muted-foreground mb-1">Avg Effort</p>
              <p className={`text-3xl font-bold ${getScoreColor(overallStats.avgEffort)}`}>
                {overallStats.avgEffort.toFixed(2)}
              </p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-sm text-muted-foreground mb-1">Avg Engagement</p>
              <p className={`text-3xl font-bold ${getScoreColor(overallStats.avgEngagement)}`}>
                {overallStats.avgEngagement.toFixed(2)}
              </p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-sm text-muted-foreground mb-1">Avg Competition</p>
              <p className={`text-3xl font-bold ${getScoreColor(overallStats.avgCompetition)}`}>
                {overallStats.avgCompetition.toFixed(2)}
              </p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-sm text-muted-foreground mb-1">Avg Attitude</p>
              <p className={`text-3xl font-bold ${getScoreColor(overallStats.avgAttitude)}`}>
                {overallStats.avgAttitude.toFixed(2)}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Trends Chart */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5" />
            30-Day Evaluation Trends
            <span className="text-sm font-normal text-muted-foreground ml-auto">
              {overallStats.totalEvaluations} total evaluations
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={350}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 12 }}
                angle={-45}
                textAnchor="end"
                height={70}
              />
              <YAxis domain={[0, 4]} ticks={[1, 2, 3, 4]} />
              <Tooltip
                content={({ active, payload }) => {
                  if (active && payload && payload.length) {
                    return (
                      <div className="bg-card text-foreground p-3 border border-border rounded shadow-lg">
                        <p className="font-medium mb-2">{payload[0].payload.date}</p>
                        <p className="text-sm text-muted-foreground mb-1">{payload[0].payload.count} evaluations</p>
                        {payload.map((entry, index) => (
                          <p key={index} className="text-sm" style={{ color: entry.color }}>
                            {entry.name}: {Number(entry.value).toFixed(2)}
                          </p>
                        ))}
                      </div>
                    );
                  }
                  return null;
                }}
              />
              <Legend />
              <Line
                type="monotone"
                dataKey="effort"
                stroke="#3b82f6"
                strokeWidth={2}
                name="Effort"
                dot={{ r: 3 }}
              />
              <Line
                type="monotone"
                dataKey="engagement"
                stroke="#10b981"
                strokeWidth={2}
                name="Engagement"
                dot={{ r: 3 }}
              />
              <Line
                type="monotone"
                dataKey="competitionLevel"
                stroke="#f59e0b"
                strokeWidth={2}
                name="Competition"
                dot={{ r: 3 }}
              />
              <Line
                type="monotone"
                dataKey="attitude"
                stroke="#8b5cf6"
                strokeWidth={2}
                name="Attitude"
                dot={{ r: 3 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Top Performers */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Star className="w-5 h-5 text-yellow-500" />
              Top Performers
            </CardTitle>
          </CardHeader>
          <CardContent>
            {topPerformers.length === 0 ? (
              <p className="text-muted-foreground text-center py-4">Not enough data yet</p>
            ) : (
              <div className="space-y-3">
                {topPerformers.map((player, index) => (
                  <div key={player.playerId} className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg border border-border">
                    <div className="w-8 h-8 rounded-full bg-yellow-500 text-white flex items-center justify-center font-bold text-sm">
                      {index + 1}
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-foreground">{player.name}</p>
                      <p className="text-xs text-muted-foreground">{player.evalCount} evaluations</p>
                    </div>
                    <Badge className={getScoreBadge(player.overall)}>
                      {player.overall.toFixed(2)}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Needs Attention */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-blue-500" />
              Needs Attention
            </CardTitle>
          </CardHeader>
          <CardContent>
            {needsAttention.length === 0 ? (
              <p className="text-muted-foreground text-center py-4">All players performing well!</p>
            ) : (
              <div className="space-y-3">
                {needsAttention.map((player) => (
                  <div key={player.playerId} className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg border border-border">
                    <div className="flex-1">
                      <p className="font-medium text-foreground">{player.name}</p>
                      <p className="text-xs text-muted-foreground">{player.evalCount} evaluations</p>
                    </div>
                    <div className="text-right">
                      <Badge className={getScoreBadge(player.overall)}>
                        {player.overall.toFixed(2)}
                      </Badge>
                      <p className="text-xs text-muted-foreground mt-1">
                        Focus: {getLowestCategory(player)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
